import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState('RUB');
  const [exchangeRates, setExchangeRates] = useState({});
  const [loading, setLoading] = useState(true);

  // Currency info for display
  const CURRENCIES = {
    'RUB': { name: 'руб', symbol: '₽' },
    'KZT': { name: 'тг', symbol: '₸' },
    'BYN': { name: 'бел. руб.', symbol: 'Br' },
    'AZN': { name: 'ман.', symbol: '₼' },
    'MDL': { name: 'лей', symbol: 'lei' },
    'GEL': { name: 'ლари', symbol: '₾' },
    'UZS': { name: 'сум', symbol: 'som' },
    'TJS': { name: 'сомони', symbol: 'sm' },
    'KGS': { name: 'сом', symbol: 'c' },
  };

  // Load saved preference on mount
  useEffect(() => {
    const savedCurrency = localStorage.getItem('preferred_currency');
    if (savedCurrency && CURRENCIES[savedCurrency]) {
      setSelectedCurrency(savedCurrency);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch and cache exchange rates
  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        // Check if we have cached rates
        const cachedRates = localStorage.getItem('currency_rates');
        const cacheTime = localStorage.getItem('currency_rates_timestamp');
        
        // If rates exist and are less than 24 hours old, use them
        if (cachedRates && cacheTime) {
          const age = Date.now() - parseInt(cacheTime);
          if (age < 24 * 60 * 60 * 1000) { // 24 hours
            const rates = JSON.parse(cachedRates);
            setExchangeRates(rates);
            setLoading(false);
            return;
          }
        }
        
        // Fetch fresh rates
        const response = await api.get('/currency-rates/');
        const rates = {};
        response.data.forEach(rate => {
          rates[rate.currency_code] = {
            rate: parseFloat(rate.rate_to_rub),
            nominal: rate.nominal
          };
        });
        
        setExchangeRates(rates);
        
        // Cache in localStorage
        localStorage.setItem('currency_rates', JSON.stringify(rates));
        localStorage.setItem('currency_rates_timestamp', Date.now().toString());
      } catch (error) {
        console.error('Error fetching exchange rates:', error);
        // Try to use cached rates even if expired
        const cachedRates = localStorage.getItem('currency_rates');
        if (cachedRates) {
          setExchangeRates(JSON.parse(cachedRates));
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchExchangeRates();
  }, []);

  const convertCurrency = (amount, fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency || !amount) {
      return parseFloat(amount) || 0;
    }
    
    // Convert to RUB first if necessary
    let amountInRub = parseFloat(amount);
    if (fromCurrency !== 'RUB') {
      const fromRate = exchangeRates[fromCurrency];
      if (!fromRate) return amountInRub;
      amountInRub = amountInRub * fromRate.rate;
    }
    
    // Convert from RUB to target currency
    if (toCurrency === 'RUB') {
      return amountInRub;
    }
    
    const toRate = exchangeRates[toCurrency];
    if (!toRate) return amountInRub;
    
    return amountInRub / toRate.rate;
  };

  const changeCurrency = (currency) => {
    setSelectedCurrency(currency);
    localStorage.setItem('preferred_currency', currency);
  };

  const getCurrencySymbol = (currencyCode) => {
    return CURRENCIES[currencyCode]?.symbol || currencyCode;
  };

  const getCurrencyName = (currencyCode) => {
    return CURRENCIES[currencyCode]?.name || currencyCode;
  };

  const value = {
    selectedCurrency,
    exchangeRates,
    loading,
    convertCurrency,
    changeCurrency,
    getCurrencySymbol,
    getCurrencyName,
    CURRENCIES,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

